/**
 * How tasks read (slice 2b spec §10): their status labels, the actions a
 * person has at each stage, the timeline's lines, the receipts line, and a
 * thread header's summary. Pure, so the pages and the thread share it and it
 * tests without rendering.
 */
import type { StatusTone } from "@crew/ui/components/status";
import { duration, tokens } from "../activity/activity-view";
import type { TaskEvent, TaskFilter, TaskItem, TaskReceipts, TaskStage } from "./api";

export type TaskStatus = { label: string; tone: StatusTone; hollow?: boolean };

/**
 * The status label (spec §10.1): Working, Queued, Waiting on <text> (hollow:
 * someone outside the crew), Stuck (destructive), Done, Canceled. Waiting on
 * a person here (slice 4 §5) is "Waiting on you" for them, else their first
 * name: `waiting_on_text` is "<first name>: <their question>".
 */
export function taskStatus(
  task: Pick<TaskItem, "stage" | "waiting_on_text"> & { waiting_on_user_id?: string | null },
  viewerId?: string,
): TaskStatus {
  switch (task.stage) {
    case "working":
      return { label: "Working", tone: "working" };
    case "queued":
      return { label: "Queued", tone: "queued" };
    case "waiting":
      if (task.waiting_on_user_id) {
        const who =
          task.waiting_on_user_id === viewerId
            ? "you"
            : (task.waiting_on_text ?? "").split(":")[0].trim() || "someone here";
        return { label: `Waiting on ${who}`, tone: "waiting" };
      }
      return task.waiting_on_text
        ? { label: `Waiting on ${task.waiting_on_text}`, tone: "queued", hollow: true }
        : { label: "Waiting", tone: "waiting" };
    case "stuck":
      return { label: "Stuck", tone: "error" };
    case "done":
      return { label: "Done", tone: "done" };
    case "canceled":
      return { label: "Canceled", tone: "queued" };
  }
}

export const isOpen = (stage: TaskStage) => stage !== "done" && stage !== "canceled";

export type TaskActions = {
  markDone: boolean;
  cancel: boolean;
  reopen: boolean;
  /** Ongoing on or off: only on open work, which the watch and follow-through look at. */
  ongoing: boolean;
};

/**
 * What a person may do (contracts §3.1, Person as the actor): Mark done and
 * Cancel any open task; Reopen a Done, Canceled or Stuck one. Hidden, not
 * disabled, where the machine wouldn't allow them (spec §10.2).
 */
export function taskActions(stage: TaskStage): TaskActions {
  const open = isOpen(stage);
  return {
    markDone: open,
    cancel: open,
    reopen: stage === "done" || stage === "canceled" || stage === "stuck",
    ongoing: open,
  };
}

export const TAB_LABELS: Record<TaskFilter, string> = {
  open: "Open",
  waiting: "Waiting on someone",
  done: "Done",
  canceled: "Canceled",
};

export const EMPTY_NOTES: Record<TaskFilter, string> = {
  open: "Nothing open. Ask for something in Chats, or start a New task.",
  waiting: "Nothing's waiting on anyone outside the crew.",
  done: "Nothing done yet. Finished tasks show here, with how they ended.",
  canceled: "Nothing canceled.",
};

const STAGE_WORDS: Record<TaskStage, string> = {
  queued: "Queued",
  working: "Working",
  waiting: "Waiting",
  done: "Done",
  stuck: "Stuck",
  canceled: "Canceled",
};

const str = (v: unknown) => (typeof v === "string" && v ? v : null);

/** A stage move's words, by where it went (and, for a few, where it came from). */
const MOVES: Partial<Record<TaskStage, (from: TaskStage | null) => string | null>> = {
  done: () => "Marked it done",
  canceled: () => "Canceled the task",
  // A person's Reopen: from Done, Canceled or Stuck.
  queued: (from) => (from === "stuck" || (from && !isOpen(from)) ? "Reopened the task" : null),
  working: (from) => (from === "queued" ? "Started on it" : null),
};

/** A stage event's line: "Moved to Waiting on insurers", "Marked it done", "Reopened the task". */
function stageLine(data: Record<string, unknown>): string {
  const from = str(data.from) as TaskStage | null;
  const to = str(data.to) as TaskStage | null;
  if (!to) return "Moved it";
  const said = MOVES[to]?.(from);
  if (said) return said;
  const on = to === "waiting" ? str(data.waiting_on_text) : null;
  return on ? `Moved to Waiting on ${on}` : `Moved to ${STAGE_WORDS[to]}`;
}

/** "Moved to Garage gym", or "Taken out of Garage gym" (slice 6d). */
function areaLine(data: Record<string, unknown>): string {
  if (data.area_id) {
    const to = str(data.area_label);
    return to ? `Moved to ${to}` : "Moved to another area";
  }
  const from = str(data.from_label);
  return from ? `Taken out of ${from}` : "Taken out of its area";
}

/** Each edited field's line, in the order they're told. */
const EDIT_LINES: [string, (data: Record<string, unknown>) => string][] = [
  ["title", (d) => (str(d.title) ? `Renamed it “${str(d.title)}”` : "Renamed it")],
  ["ongoing", (d) => (d.ongoing === false ? "No longer ongoing" : "Marked it ongoing")],
  [
    "waiting_on_text",
    (d) =>
      str(d.waiting_on_text)
        ? `Now waiting on ${str(d.waiting_on_text)}`
        : "Changed who it's waiting on",
  ],
  ["area_id", areaLine],
];

/** An edit's line: a rename, Ongoing on or off, who it waits on now, or its Area (slice 6d). */
function editLine(data: Record<string, unknown>): string {
  const fields: unknown[] = Array.isArray(data.fields) ? data.fields : [];
  const lines = EDIT_LINES.filter(([field]) => fields.includes(field)).map(([, line]) =>
    line(data),
  );
  return lines.join(" · ") || "Edited the task";
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A nudge's line: "Checked in after 2 quiet days", once PR 4's nudge carries its days. */
function nudgeLine(data: Record<string, unknown>): string {
  const days = typeof data.days === "number" ? data.days : null;
  return days ? `Checked in after ${plural(days, "quiet day")}` : "Checked in after a quiet spell";
}

export type TimelineLine = {
  /** What happened, in a few words. */
  line: string;
  /** The note it carried: the outcome, why, or the note itself. */
  note: string | null;
};

/** One event on Task detail's "What's happened" (spec §10.2). */
export function eventLine(event: Pick<TaskEvent, "kind" | "text" | "data">): TimelineLine {
  const note = event.text.trim() || null;
  switch (event.kind) {
    case "created":
      return { line: "Opened the task", note: null };
    case "stage":
      return { line: stageLine(event.data), note };
    case "note":
      return { line: note ?? "Added a note", note: null };
    case "edited":
      return { line: editLine(event.data), note };
    case "nudged":
      return { line: nudgeLine(event.data), note };
    case "dispatched":
      return {
        line: event.data.kind === "start" ? "Asked to start it" : "Asked to carry on",
        note,
      };
    // Slice 3 §4.3 (Devin on #499): an approval held on the task, and how it closed.
    case "approval_opened":
      return { line: "Held something for approval", note };
    case "approval_closed":
      return { line: CLOSED[String(event.data.status)] ?? "An approval closed", note };
  }
}

/** How an approval on the task closed, by its status. */
const CLOSED: Record<string, string> = {
  done: "Approved, and it ran",
  failed: "Approved, but it didn't run",
  unknown: "Approved; whether it ran isn't known",
  declined: "Asked for a change",
  withdrawn: "Withdrew an approval",
  expired: "An approval expired",
};

/** "6 min on the computer", "40 s on the computer". */
function computerTime(ms: number): string {
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  return duration(ms);
}

/**
 * "Worked on in 4 turns · 38k tokens · 6 min on the computer · 3 web lookups"
 * (spec §10.2), with "includes turns shared with other tasks" when one is.
 */
export function receiptsLine(r: TaskReceipts): string {
  if (r.turns === 0) return "Not worked on yet";
  const parts = [`Worked on in ${plural(r.turns, "turn")}`, `${tokens(r.tokens)} tokens`];
  if (r.computer_ms > 0) parts.push(`${computerTime(r.computer_ms)} on the computer`);
  if (r.web > 0) parts.push(plural(r.web, "web lookup"));
  if (r.shared) parts.push("includes turns shared with other tasks");
  return parts.join(" · ");
}

/** A thread header's tasks (spec §5): one open as "Task · Working", more as "2 tasks". */
export function threadTasksLabel(tasks: TaskItem[] | undefined): string | null {
  const open = (tasks ?? []).filter((t) => isOpen(t.stage));
  if (open.length === 0) return null;
  if (open.length === 1) return `Task · ${taskStatus(open[0]).label}`;
  return `${open.length} tasks`;
}

/** Where a thread header's tasks link: the one open task, else the Tasks page. */
export function threadTasksHref(tasks: TaskItem[] | undefined): string {
  const open = (tasks ?? []).filter((t) => isOpen(t.stage));
  return open.length === 1 ? `/tasks/${open[0].id}` : "/tasks";
}

/** The system lines about a task (spec §5), which link to it. */
const TASK_LINES = new Set([
  "task_link",
  "task_canceled",
  "task_reopened",
  "follow_through",
  "nudge",
]);

/** A system line's task, when it's one of the task lines. */
export function lineTask(message: { kind: string; data: Record<string, unknown> }): string | null {
  if (message.kind !== "system" || !TASK_LINES.has(String(message.data.event))) return null;
  return str(message.data.task_id);
}
